import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { 
  Plus, Search, Calendar as CalendarIcon, Send, Check, X, FileText, ArrowRight, 
  Trash2, Download, Eye, Type, Package, RefreshCw,
  ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, Clock
} from "lucide-react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { sv } from "date-fns/locale";
import { CustomerSelector } from "@/components/CustomerSelector";
import { InvoicePreview } from "@/components/InvoicePreview";
import { PreviewModal } from "@/components/PreviewModal";

const Quotes = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [company, setCompany] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Multi-select
  const [selectedQuotes, setSelectedQuotes] = useState(new Set());
  
  // Sorting
  const [sortColumn, setSortColumn] = useState("quote_date");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    quote_number: "",
    customer_name: "",
  });
  
  const [formData, setFormData] = useState({
    customer_id: "",
    customer_name: "",
    quote_date: format(new Date(), "yyyy-MM-dd"),
    valid_until: format(addDays(new Date(), 30), "yyyy-MM-dd"),
    our_reference: user?.name || "",
    your_reference: "",
    lines: [{ description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, discount_percent: 0 }],
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, customersRes, articlesRes, companyRes] = await Promise.all([
        axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/articles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/company`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setQuotes(quotesRes.data);
      setCustomers(customersRes.data);
      setArticles(articlesRes.data);
      if (companyRes.data) setCompany(companyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kunde inte hämta data");
    } finally {
      setLoading(false);
    }
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="ml-1 text-slate-400" />;
    return sortDirection === "asc" 
      ? <ArrowUp size={14} className="ml-1 text-indigo-600" />
      : <ArrowDown size={14} className="ml-1 text-indigo-600" />;
  };

  // Selection
  const toggleSelectAll = () => {
    if (selectedQuotes.size === filteredQuotes.length) {
      setSelectedQuotes(new Set());
    } else {
      setSelectedQuotes(new Set(filteredQuotes.map(q => q.id)));
    }
  };

  const toggleQuote = (id) => {
    const newSelected = new Set(selectedQuotes);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedQuotes(newSelected);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (selectedQuotes.size === 0) return;
    
    let deleted = 0;
    let failed = 0;
    
    for (const id of selectedQuotes) {
      const quote = quotes.find(q => q.id === id);
      if (quote?.status === "converted") {
        failed++;
        continue;
      }
      try {
        await axios.delete(`${API}/quotes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch {
        failed++;
      }
    }
    
    if (deleted > 0) toast.success(`${deleted} offerter borttagna`);
    if (failed > 0) toast.error(`${failed} kunde inte tas bort`);
    
    setSelectedQuotes(new Set());
    fetchData();
  };

  const handleBulkMarkSent = async () => {
    if (selectedQuotes.size === 0) return;
    
    let updated = 0;
    let failed = 0;
    
    for (const id of selectedQuotes) {
      const quote = quotes.find(q => q.id === id);
      if (quote?.status !== "draft") {
        failed++;
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("status", "sent");
        await axios.put(`${API}/quotes/${id}/status`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updated++;
      } catch {
        failed++;
      }
    }
    
    if (updated > 0) toast.success(`${updated} offerter markerade som skickade`);
    if (failed > 0) toast.error(`${failed} kunde inte uppdateras`);
    
    setSelectedQuotes(new Set());
    fetchData();
  };

  const handleBulkMarkAccepted = async () => {
    if (selectedQuotes.size === 0) return;
    
    let updated = 0;
    let failed = 0;
    
    for (const id of selectedQuotes) {
      const quote = quotes.find(q => q.id === id);
      if (quote?.status !== "sent") {
        failed++;
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("status", "accepted");
        await axios.put(`${API}/quotes/${id}/status`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updated++;
      } catch {
        failed++;
      }
    }
    
    if (updated > 0) toast.success(`${updated} offerter markerade som accepterade`);
    if (failed > 0) toast.error(`${failed} kunde inte uppdateras`);
    
    setSelectedQuotes(new Set());
    fetchData();
  };

  const handleAddLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, discount_percent: 0, is_freetext: false }]
    });
  };

  const handleRemoveLine = (index) => {
    if (formData.lines.length <= 1) return;
    setFormData({
      ...formData,
      lines: formData.lines.filter((_, i) => i !== index)
    });
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
  };

  const handleToggleLineType = (index) => {
    const newLines = [...formData.lines];
    newLines[index].is_freetext = !newLines[index].is_freetext;
    if (newLines[index].is_freetext) {
      newLines[index].article_id = null;
    }
    setFormData({ ...formData, lines: newLines });
  };

  const handleArticleSelect = (index, articleId) => {
    const article = articles.find(a => a.id === articleId);
    if (article) {
      const newLines = [...formData.lines];
      newLines[index] = {
        ...newLines[index],
        article_id: articleId,
        description: article.name,
        unit: article.unit,
        unit_price: article.price,
        vat_rate: article.vat_rate
      };
      setFormData({ ...formData, lines: newLines });
    }
  };

  const handleCustomerCreated = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setFormData(prev => ({
      ...prev,
      customer_id: newCustomer.id,
      customer_name: newCustomer.name
    }));
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData(prev => ({ 
      ...prev, 
      customer_id: customerId,
      customer_name: customer?.name || ""
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quotes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offert skapad");
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa offert");
    }
  };

  const handleStatusChange = async (quoteId, status) => {
    try {
      const formData = new FormData();
      formData.append("status", status);
      await axios.put(`${API}/quotes/${quoteId}/status`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status uppdaterad");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte uppdatera status");
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/convert-to-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offert omvandlad till faktura");
      fetchData();
    } catch (error) {
      toast.error("Kunde inte omvandla offert");
    }
  };

  const handleDelete = async (quoteId) => {
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offert borttagen");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte ta bort offert");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      quote_date: format(new Date(), "yyyy-MM-dd"),
      valid_until: format(addDays(new Date(), 30), "yyyy-MM-dd"),
      our_reference: user?.name || "",
      your_reference: "",
      lines: [{ description: "", quantity: 1, unit: "st", unit_price: 0, vat_rate: 25, discount_percent: 0 }],
      notes: ""
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: <Badge className="badge-pending">{t('quotes.status.draft')}</Badge>,
      sent: <Badge className="bg-blue-100 text-blue-700">{t('quotes.status.sent')}</Badge>,
      accepted: <Badge className="badge-success">{t('quotes.status.accepted')}</Badge>,
      rejected: <Badge className="badge-error">{t('quotes.status.rejected')}</Badge>,
      expired: <Badge className="bg-slate-100 text-slate-700">{t('quotes.status.expired')}</Badge>,
      converted: <Badge className="bg-green-100 text-green-700">{t('nav.invoices')}</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const calculateTotal = () => {
    let subtotal = 0;
    let vat = 0;
    formData.lines.forEach(line => {
      const lineTotal = line.quantity * line.unit_price * (1 - (line.discount_percent || 0) / 100);
      subtotal += lineTotal;
      vat += lineTotal * (line.vat_rate / 100);
    });
    return { subtotal, vat, total: subtotal + vat };
  };

  const totals = calculateTotal();

  // Filtering and sorting
  const filteredQuotes = useMemo(() => {
    return quotes
      .filter(q => {
        const matchesSearch = !searchTerm ||
          q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === "all" || q.status === filterStatus;
        
        const matchesQuoteNumber = !columnFilters.quote_number ||
          q.quote_number?.toLowerCase().includes(columnFilters.quote_number.toLowerCase());
        const matchesCustomer = !columnFilters.customer_name ||
          q.customer_name?.toLowerCase().includes(columnFilters.customer_name.toLowerCase());
        
        return matchesSearch && matchesStatus && matchesQuoteNumber && matchesCustomer;
      })
      .sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];
        
        if (sortColumn === "total") {
          aVal = a.total || 0;
          bVal = b.total || 0;
        } else if (typeof aVal === 'string') {
          aVal = aVal?.toLowerCase() || "";
          bVal = bVal?.toLowerCase() || "";
        }
        
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
  }, [quotes, searchTerm, filterStatus, columnFilters, sortColumn, sortDirection]);

  // Statistics
  const stats = useMemo(() => {
    const total = quotes.reduce((s, q) => s + (q.total || 0), 0);
    const acceptedTotal = quotes.filter(q => q.status === "accepted").reduce((s, q) => s + (q.total || 0), 0);
    const sentCount = quotes.filter(q => q.status === "sent").length;
    const acceptedCount = quotes.filter(q => q.status === "accepted").length;
    const draftCount = quotes.filter(q => q.status === "draft").length;
    const conversionRate = quotes.length > 0 ? Math.round((acceptedCount / quotes.length) * 100) : 0;
    
    return { total, acceptedTotal, sentCount, acceptedCount, draftCount, conversionRate };
  }, [quotes]);

  // Export
  const exportQuotes = () => {
    const csv = [
      ["Offertnr", "Kund", "Datum", "Giltig till", "Belopp", "Status"].join(";"),
      ...filteredQuotes.map(q => [
        q.quote_number,
        q.customer_name,
        q.quote_date,
        q.valid_until,
        q.total,
        q.status
      ].join(";"))
    ].join("\n");
    
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `offerter_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export klar!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="quotes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('quotes.title')}</h1>
          <p className="text-slate-600">Hantera alla dina offerter</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportQuotes}>
            <Download size={16} className="mr-2" />
            Exportera
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-quote-btn">
                <Plus size={16} className="mr-2" />
                {t('quotes.newQuote')}
              </Button>
            </DialogTrigger>
            <DialogContent size="full" className="max-w-[95vw] max-h-[95vh] p-0">
              <div className="flex h-[95vh]">
                {/* Left side - Form */}
                <div className="w-1/2 p-6 overflow-y-auto border-r border-slate-200">
                  <DialogHeader className="mb-4">
                    <DialogTitle>{t('quotes.newQuote')}</DialogTitle>
                    <DialogDescription>{t('quotes.subtitle')}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('invoices.customer')} *</Label>
                        <CustomerSelector
                          customers={customers}
                          value={formData.customer_id}
                          onChange={handleCustomerSelect}
                          onCustomerCreated={handleCustomerCreated}
                          placeholder={t('customers.newCustomer') + '...'}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label>{t('common.date')}</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formData.quote_date}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={new Date(formData.quote_date)}
                                onSelect={(date) => date && setFormData({ ...formData, quote_date: format(date, "yyyy-MM-dd") })}
                                locale={sv}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label>{t('quotes.validUntil')}</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formData.valid_until}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={new Date(formData.valid_until)}
                                onSelect={(date) => date && setFormData({ ...formData, valid_until: format(date, "yyyy-MM-dd") })}
                                locale={sv}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('invoices.ourReference')}</Label>
                        <Input
                          value={formData.our_reference}
                          onChange={(e) => setFormData({ ...formData, our_reference: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('invoices.yourReference')}</Label>
                        <Input
                          value={formData.your_reference}
                          onChange={(e) => setFormData({ ...formData, your_reference: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Quote lines */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label>{t('invoices.lines')}</Label>
                        <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                          <Plus size={14} className="mr-1" />
                          {t('invoices.addLine')}
                        </Button>
                      </div>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-2 w-10">Typ</th>
                              <th className="text-left p-2 w-24">{t('invoices.article')}</th>
                              <th className="text-left p-2">{t('common.description')}</th>
                              <th className="text-right p-2 w-20">{t('invoices.quantity')}</th>
                              <th className="text-right p-2 w-24">{t('invoices.unitPrice')}</th>
                              <th className="text-right p-2 w-20">{t('invoices.vat')}</th>
                              <th className="text-right p-2 w-24">{t('common.total')}</th>
                              <th className="w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.lines.map((line, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleLineType(idx)}
                                    className={`h-8 w-8 p-0 ${line.is_freetext ? 'text-blue-600 bg-blue-50' : 'text-indigo-600 bg-indigo-50'}`}
                                    title={line.is_freetext ? 'Fritext (klicka för artikel)' : 'Artikel (klicka för fritext)'}
                                  >
                                    {line.is_freetext ? <Type size={14} /> : <Package size={14} />}
                                  </Button>
                                </td>
                                <td className="p-2">
                                  {!line.is_freetext ? (
                                    <Select onValueChange={(v) => handleArticleSelect(idx, v)} value={line.article_id || ""}>
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Välj..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {articles.map(a => (
                                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs text-slate-400 italic">Fritext</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {line.is_freetext ? (
                                    <textarea
                                      value={line.description}
                                      onChange={(e) => handleLineChange(idx, "description", e.target.value)}
                                      className="w-full h-16 text-sm border rounded-md p-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      placeholder="Skriv valfri text..."
                                    />
                                  ) : (
                                    <Input
                                      value={line.description}
                                      onChange={(e) => handleLineChange(idx, "description", e.target.value)}
                                      className="h-8"
                                    />
                                  )}
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    value={line.quantity}
                                    onChange={(e) => handleLineChange(idx, "quantity", parseFloat(e.target.value) || 0)}
                                    className="h-8 text-right"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    value={line.unit_price}
                                    onChange={(e) => handleLineChange(idx, "unit_price", parseFloat(e.target.value) || 0)}
                                    className="h-8 text-right"
                                  />
                                </td>
                                <td className="p-2">
                                  <Select
                                    value={line.vat_rate.toString()}
                                    onValueChange={(v) => handleLineChange(idx, "vat_rate", parseInt(v))}
                                  >
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="25">25%</SelectItem>
                                      <SelectItem value="12">12%</SelectItem>
                                      <SelectItem value="6">6%</SelectItem>
                                      <SelectItem value="0">0%</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="p-2 text-right font-mono">
                                  {formatCurrency(line.quantity * line.unit_price)}
                                </td>
                                <td className="p-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveLine(idx)}
                                    className="text-red-500"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50 font-medium">
                            <tr className="border-t">
                              <td colSpan="6" className="p-2 text-right">{t('invoices.subtotal')}:</td>
                              <td className="p-2 text-right font-mono">{formatCurrency(totals.subtotal)}</td>
                              <td></td>
                            </tr>
                            <tr>
                              <td colSpan="6" className="p-2 text-right">{t('invoices.vat')}:</td>
                              <td className="p-2 text-right font-mono">{formatCurrency(totals.vat)}</td>
                              <td></td>
                            </tr>
                            <tr className="text-lg">
                              <td colSpan="6" className="p-2 text-right font-bold">{t('common.total')}:</td>
                              <td className="p-2 text-right font-mono font-bold">{formatCurrency(totals.total)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('invoices.notes')}</Label>
                      <Input
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder={t('invoices.notes')}
                      />
                    </div>

                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                        {t('common.cancel')}
                      </Button>
                      <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={!formData.customer_id}>
                        {t('quotes.newQuote')}
                      </Button>
                    </DialogFooter>
                  </form>
                </div>
                
                {/* Right side - Live Preview */}
                <div className="w-1/2 bg-slate-100 p-6 overflow-y-auto">
                  <div className="sticky top-0">
                    <h3 className="text-sm font-semibold text-slate-600 mb-4 flex items-center gap-2">
                      <Eye size={16} />
                      Live-förhandsgranskning
                    </h3>
                    <InvoicePreview 
                      invoice={{
                        ...formData,
                        quote_number: "UTKAST"
                      }} 
                      company={company}
                      type="quote"
                      scale={0.65}
                    />
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <FileText className="text-indigo-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{quotes.length}</p>
                <p className="text-xs text-slate-500">Totalt offerter</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">{formatCurrency(stats.total)}</p>
                <p className="text-xs text-slate-500">Totalt värde</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Send className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-xl font-bold text-blue-600">{stats.sentCount}</p>
                <p className="text-xs text-slate-500">Skickade</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Check className="text-green-600" size={16} />
              <div>
                <p className="text-xl font-bold text-green-600">{stats.acceptedCount}</p>
                <p className="text-xs text-slate-500">Accepterade</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-purple-600" size={16} />
              <div>
                <p className="text-xl font-bold text-purple-600">{stats.conversionRate}%</p>
                <p className="text-xs text-slate-500">Konvertering</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions Bar */}
      {selectedQuotes.size > 0 && (
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-indigo-900">
                {selectedQuotes.size} offerter valda
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedQuotes(new Set())}>
                  Avmarkera
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkMarkSent}
                  className="text-blue-600 border-blue-300"
                >
                  <Send size={14} className="mr-1" />
                  Markera skickade
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkMarkAccepted}
                  className="text-green-600 border-green-300"
                >
                  <Check size={14} className="mr-1" />
                  Markera accepterade
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 border-red-300"
                  onClick={handleBulkDelete}
                >
                  <Trash2 size={14} className="mr-1" />
                  Ta bort valda
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters & Search */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input 
                placeholder="Sök offertnr, kund..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla status</SelectItem>
                <SelectItem value="draft">Utkast</SelectItem>
                <SelectItem value="sent">Skickade</SelectItem>
                <SelectItem value="accepted">Accepterade</SelectItem>
                <SelectItem value="rejected">Avvisade</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
                setColumnFilters({ quote_number: "", customer_name: "" });
              }}
            >
              <X size={14} className="mr-1" />
              Rensa filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quotes Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span>Offertlista ({filteredQuotes.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredQuotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox 
                        checked={selectedQuotes.size === filteredQuotes.length && filteredQuotes.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("quote_number")}>
                        Offertnr {getSortIcon("quote_number")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("customer_name")}>
                        Kund {getSortIcon("customer_name")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("quote_date")}>
                        Datum {getSortIcon("quote_date")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("valid_until")}>
                        Giltig till {getSortIcon("valid_until")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button className="flex items-center justify-end hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("total")}>
                        Belopp {getSortIcon("total")}
                      </button>
                    </th>
                    <th className="text-center p-3">
                      <button className="flex items-center justify-center hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("status")}>
                        Status {getSortIcon("status")}
                      </button>
                    </th>
                    <th className="text-right p-3 font-medium">Åtgärder</th>
                  </tr>
                  {/* Column Filters */}
                  <tr className="bg-slate-100/50">
                    <th className="p-1"></th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.quote_number}
                        onChange={(e) => setColumnFilters({...columnFilters, quote_number: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.customer_name}
                        onChange={(e) => setColumnFilters({...columnFilters, customer_name: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((quote) => {
                    const formattedNumber = quote.quote_number || "-";
                    return (
                      <tr 
                        key={quote.id} 
                        className={`border-b border-slate-100 hover:bg-slate-50 ${selectedQuotes.has(quote.id) ? 'bg-indigo-50' : ''}`}
                      >
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedQuotes.has(quote.id)}
                            onCheckedChange={() => toggleQuote(quote.id)}
                          />
                        </td>
                        <td className="p-3 font-mono font-medium text-indigo-600">{formattedNumber}</td>
                        <td className="p-3">{quote.customer_name || "-"}</td>
                        <td className="p-3 text-slate-600">{quote.quote_date}</td>
                        <td className="p-3 text-slate-600">{quote.valid_until}</td>
                        <td className="p-3 text-right font-medium">{formatCurrency(quote.total)}</td>
                        <td className="p-3 text-center">{getStatusBadge(quote.status)}</td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            {quote.status === "draft" && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleStatusChange(quote.id, "sent")} title="Markera som skickad">
                                  <Send size={14} />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(quote.id)} className="text-red-500">
                                  <Trash2 size={14} />
                                </Button>
                              </>
                            )}
                            {quote.status === "sent" && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleStatusChange(quote.id, "accepted")} className="text-green-600" title="Markera accepterad">
                                  <Check size={14} />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleStatusChange(quote.id, "rejected")} className="text-red-500" title="Markera avvisad">
                                  <X size={14} />
                                </Button>
                              </>
                            )}
                            {quote.status === "accepted" && !quote.converted_to_invoice_id && (
                              <Button variant="ghost" size="sm" onClick={() => handleConvertToInvoice(quote.id)} className="text-indigo-600" title="Omvandla till faktura">
                                <ArrowRight size={14} />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => setPreviewQuote(quote)}
                              title="Förhandsgranska"
                            >
                              <Eye size={14} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => window.open(`${API}/quotes/${quote.id}/pdf`, '_blank')}
                              title="Ladda ner PDF"
                              className="text-indigo-600"
                            >
                              <Download size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <FileText className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Inga offerter hittades</p>
              <p className="text-sm mt-1">Skapa din första offert för att komma igång</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <PreviewModal
        open={!!previewQuote}
        onClose={() => setPreviewQuote(null)}
        invoice={previewQuote || {}}
        company={company}
        type="quote"
      />
    </div>
  );
};

export default Quotes;
